import { Link } from 'react-router-dom';
import { Shuffle } from 'lucide-react';
import type { Track } from '@pujolisique/shared';
import { fetchRandomTracks, useHome, useMe, useStories } from '../api/hooks';
import { useAccess } from '../lib/access';
import { SeriesCard } from './Stories';
import { usePlayer, currentTrack } from '../player/store';
import { AlbumCard, PersonCard, Shelf } from '../components/Cards';
import { Cover } from '../components/Cover';
import { ErrorBox, Loading } from '../components/States';
import { Bars } from '../components/TrackList';
import { artistLine, greeting } from '../lib/format';
import { toast } from '../ui/toast';

export function HomePage() {
  const me = useMe();
  const home = useHome();
  const { hasMusic, hasStories } = useAccess();
  const stories = useStories();
  const playTracks = usePlayer((s) => s.playTracks);

  const shuffleAll = async () => {
    try {
      const r = await fetchRandomTracks();
      playTracks(r.items, 0, { source: 'Toute la bibliothèque', shuffle: false });
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };

  return (
    <div className="page page--home">
      <header className="home__head">
        <h1 className="home__greet">{greeting(me.data?.name)}</h1>
        {hasMusic && (
          <button className="btn btn--gold" onClick={shuffleAll}>
            <Shuffle size={17} /> Lecture aléatoire de la bibliothèque
          </button>
        )}
      </header>

      {home.isLoading && <Loading />}
      {home.error && <ErrorBox error={home.error} retry={() => home.refetch()} />}
      {home.data && (
        <>
          {home.data.continueStories.length > 0 && (
            <Shelf title="Reprendre les histoires" to="/stories">
              {home.data.continueStories.map((s) => (
                <SeriesCard key={s.id} series={s} showResume />
              ))}
            </Shelf>
          )}

          {home.data.recentlyPlayed.length > 0 && <QuickPicks tracks={home.data.recentlyPlayed.slice(0, 8)} />}

          {home.data.recentlyAdded.length > 0 && (
            <Shelf title="Ajoutés récemment" to="/albums?sort=newest">
              {home.data.recentlyAdded.map((a) => (
                <AlbumCard key={a.id} album={a} />
              ))}
            </Shelf>
          )}

          {home.data.composers.length > 0 && (
            <Shelf title="Compositeurs" sub="Un mix de leurs œuvres en un clic" to="/composers">
              {home.data.composers.map((c) => (
                <PersonCard key={c.id} person={c} kind="composer" />
              ))}
            </Shelf>
          )}

          {home.data.mostPlayed.length > 0 && (
            <Shelf title="Vos albums les plus écoutés" to="/albums?sort=frequent">
              {home.data.mostPlayed.map((a) => (
                <AlbumCard key={a.id} album={a} />
              ))}
            </Shelf>
          )}

          {home.data.discover.length > 0 && (
            <Shelf title="À redécouvrir" sub="Une sélection au hasard dans la bibliothèque" to="/albums?sort=random">
              {home.data.discover.map((a) => (
                <AlbumCard key={a.id} album={a} />
              ))}
            </Shelf>
          )}

          {hasStories && (stories.data?.length ?? 0) > 0 && (
            <Shelf title={hasMusic ? 'Histoires' : 'Toutes les histoires'} to="/stories">
              {(stories.data ?? []).slice(0, 18).map((s) => (
                <SeriesCard key={s.id} series={s} />
              ))}
            </Shelf>
          )}

          {hasMusic && !home.data.recentlyAdded.length && !home.data.discover.length && (
            <div className="state">
              <p className="state__title">La bibliothèque est vide</p>
              <p className="state__text">Ajoutez des fichiers audio au serveur musical puis lancez une analyse de la bibliothèque.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Grille compacte « Reprendre » : les derniers titres écoutés */
function QuickPicks({ tracks }: { tracks: Track[] }) {
  const playTracks = usePlayer((s) => s.playTracks);
  const current = usePlayer(currentTrack);
  const playing = usePlayer((s) => s.playing);
  return (
    <section className="quick">
      <div className="shelf__head">
        <h2 className="shelf__title">
          <Link to="/history">Écoutés récemment</Link>
        </h2>
      </div>
      <div className="quick__grid">
        {tracks.map((t, i) => (
          <button key={t.id} className={`quick__item ${current?.id === t.id ? 'is-current' : ''}`} onClick={() => playTracks(tracks, i, { source: 'Écoutés récemment' })}>
            <Cover art={t.cover} name={t.album ?? t.title} size={128} />
            <span className="quick__text">
              <span className="quick__title">{t.title}</span>
              <span className="quick__sub">{artistLine(t)}</span>
            </span>
            {current?.id === t.id && playing && <Bars />}
          </button>
        ))}
      </div>
    </section>
  );
}
